/**
 * spring-preloader.js — branded "113 SPRING" preloader behavior.
 *
 * Self-contained custom element with ZERO library dependencies — the intro
 * runs on the Web Animations API (element.animate) and the exit on the CSS
 * transitions inlined in the snippet, so nothing waits on a vendor download.
 * Two beats: the letters fade in spread across the viewport (3x3, anchored
 * to the edges), then travel inward and settle as the square "113 / SPR /
 * ING" brand-logo lockup (LOGO-113SPRING.svg). Every glyph SVG carries the
 * full 409x406 logo viewBox with only its own letter, so the settled END
 * state is one shared centered transform — the letters compose the exact
 * logo by construction. The reveal splits the curtain open (top/bottom
 * halves on desktop, left/right halves on mobile) via the .is-leaving CSS.
 * Both beats move every glyph together (no stagger).
 *
 * "Functional": the reveal is gated on REAL readiness (window load +
 * document.fonts + the grid having spread), raced against a hard safety
 * timeout so a slow asset can never trap a visitor. A thin progress bar /
 * counter tracks real load.
 *
 * Markup, inlined critical CSS, and the synchronous boot script (scroll lock,
 * once-per-session skip, failsafe) live in snippets/spring-preloader.liquid.
 */
(function () {
  'use strict';

  if (!('customElements' in window) || customElements.get('spring-preloader')) return;

  var REDUCED = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var WAAPI = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  var EASE_INOUT = 'cubic-bezier(0.645, 0.045, 0.355, 1)'; /* ~easeInOutCubic */
  var EASE_OUT = 'cubic-bezier(0.5, 1, 0.89, 1)'; /* ~easeOutQuad */

  /* Brand-logo geometry (LOGO-113SPRING.svg, viewBox 0 0 409 406). Each glyph
     box carries that full viewBox, so only the scatter beat needs per-letter
     data: LOGO_OFF[i] = the letter's centre offset from the logo-box centre
     (logo units, row-major 1 1 3 / S P R / I N G), used to anchor the LETTER
     (not its box) to the edge grid. LOGO_LETTER_H = tallest letter height. */
  var LOGO_H = 406;
  var LOGO_LETTER_H = 81.4;
  var LOGO_OFF = [
    [-175.6, -162.4], [3.1, -162.4], [172.3, -162.3],
    [-172.8, -0.1],   [0.3, 0],      [174.0, -0.1],
    [-174.9, 161.9],  [2.0, 162.3],  [170.7, 162.3]
  ];

  function clamp(min, val, max) { return Math.max(min, Math.min(val, max)); }

  function pageLoadedPromise() {
    if (document.readyState === 'complete') return Promise.resolve();
    return new Promise(function (res) { window.addEventListener('load', res, { once: true }); });
  }

  function fontsReadyPromise() {
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      return document.fonts.ready.catch(function () {});
    }
    return Promise.resolve();
  }

  function timeoutPromise(ms, value) {
    return new Promise(function (res) { setTimeout(function () { res(value); }, ms); });
  }

  /* Center-anchored placement: move the glyph's center to (x, y), then scale
     about that center. Order matters — percent translate is of the glyph box. */
  function transformFor(c) {
    return 'translate(' + c.x + 'px, ' + c.y + 'px) translate(-50%, -50%) scale(' + c.scale + ')';
  }

  class SpringPreloader extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      try { clearTimeout(window.__springPreloaderFailsafe); } catch (e) {}

      try {
        this.docEl = document.documentElement;

        // Defensive: theme editor or an explicit session-skip -> remove, no animation.
        if ((window.Shopify && window.Shopify.designMode) || this.docEl.hasAttribute('data-spring-preloader-skip')) {
          this.cleanup(true);
          return;
        }

        this.stage = this.querySelector('.spring-preloader__stage');
        this.glyphs = Array.prototype.slice.call(this.querySelectorAll('.spring-preloader__glyph'));
        this.fill = this.querySelector('.spring-preloader__progress-fill');
        this.counterEl = this.querySelector('.spring-preloader__counter');
        this.progressStyle = this.getAttribute('data-progress-style') || 'none';
        this.duration = parseInt(this.getAttribute('data-duration'), 10) || 2800;
        this.maxWait = parseInt(this.getAttribute('data-max-wait'), 10) || 5000;
        this._progress = 0;
        this._counterVal = 0;
        this._anims = [];

        // Mark seen so the next navigation this session can skip (once-per-session).
        try { sessionStorage.setItem('spring_preloader_seen', '1'); } catch (e) {}

        if (!this.stage || !this.glyphs.length) { this.cleanup(true); return; }

        if (REDUCED || !WAAPI) { this.runReduced(); return; }
        this.run();
      } catch (err) {
        try { this.cleanup(true); } catch (e) {}
      }
    }

    /* Natural glyph-box height at the base size, before any transform (every
       box shares the full logo viewBox, so one height describes them all). */
    measure() {
      var rect = this.glyphs[0].getBoundingClientRect();
      this.baseH = rect.height || 300;
    }

    /* Two responsive keyframe coordinate sets (viewport px): scatter (letters
       anchored to the viewport edges, the START) + gather (the brand-logo
       lockup, the END resting form). A single scale is used for both beats, so
       the move reads as a pure inward translation — the start and end letter
       sizes look the same. */
    computeCoords() {
      var vw = window.innerWidth;
      var vh = window.innerHeight;

      // Lockup box size — mirrors the static CSS --pl-lockup (keep in sync).
      var L = clamp(140, Math.min(vw * 0.24, vh * 0.22), 175);
      var scale = L / this.baseH;
      var u = L / LOGO_H; /* rendered px per logo unit */
      var letterH = LOGO_LETTER_H * u;

      var scatter = [];
      var gather = [];
      var i, r, c;

      // Scatter (START): letter centres on a 3x3 grid pushed toward the
      // corners/edges of the viewport (edge-anchored columns/rows 0, 0.5, 1;
      // half the letter height added to the inset for a true edge gap). The
      // transform positions the BOX centre, so subtract the letter's offset
      // inside its full-logo box to anchor the letter itself to the grid.
      var inset = Math.max(28, Math.min(vw, vh) * 0.06);
      var scatterMargin = inset + letterH * 0.5;
      for (i = 0; i < 9; i++) {
        r = Math.floor(i / 3);
        c = i % 3;
        scatter.push({
          x: scatterMargin + (vw - 2 * scatterMargin) * (c / 2) - LOGO_OFF[i][0] * u,
          y: scatterMargin + (vh - 2 * scatterMargin) * (r / 2) - LOGO_OFF[i][1] * u,
          scale: scale
        });
        // Gather (END): every full-logo box centred on the viewport — the
        // letters compose the exact logo arrangement.
        gather.push({ x: vw / 2, y: vh / 2, scale: scale });
      }

      this.coords = { scatter: scatter, gather: gather };
    }

    setProgress(p) {
      p = Math.max(this._progress, clamp(0, p, 1));
      this._progress = p;
      if (this.progressStyle === 'bar' && this.fill) {
        // The fill carries a CSS transform transition; just set the target.
        this.fill.style.transform = 'scaleX(' + p + ')';
      } else if (this.progressStyle === 'counter' && this.counterEl) {
        this.tweenCounter(p * 100);
      }
    }

    /* Small rAF lerp so the counter rolls instead of jumping between ticks. */
    tweenCounter(target) {
      var self = this;
      this._counterTarget = Math.max(this._counterTarget || 0, target);
      if (this._counterRaf) return;
      function step() {
        var next = self._counterVal + (self._counterTarget - self._counterVal) * 0.14;
        if (self._counterTarget - next < 0.5) next = self._counterTarget;
        self._counterVal = next;
        self.counterEl.textContent = Math.round(next) + '%';
        if (next < self._counterTarget) {
          self._counterRaf = requestAnimationFrame(step);
        } else {
          self._counterRaf = null;
        }
      }
      this._counterRaf = requestAnimationFrame(step);
    }

    /* Drive the indicator from real signals: eager-image load fraction + font + load milestones. */
    setupProgress() {
      if (this.progressStyle === 'none') return;
      var self = this;
      var imgs = Array.prototype.slice.call(document.images || []).filter(function (img) {
        return img.loading !== 'lazy';
      });
      this._fontsDone = false;
      this._loadDone = false;

      function compute() {
        var loaded = 0;
        for (var k = 0; k < imgs.length; k++) { if (imgs[k].complete) loaded++; }
        var imgFrac = imgs.length ? loaded / imgs.length : 1;
        var p = imgFrac * 0.6 + (self._fontsDone ? 0.2 : 0) + (self._loadDone ? 0.2 : 0);
        self.setProgress(p);
      }

      imgs.forEach(function (img) {
        if (!img.complete) {
          img.addEventListener('load', compute, { once: true });
          img.addEventListener('error', compute, { once: true });
        }
      });
      fontsReadyPromise().then(function () { self._fontsDone = true; compute(); });
      pageLoadedPromise().then(function () { self._loadDone = true; compute(); });

      this._progressTick = setInterval(compute, 250);
      compute();
    }

    /* Choreography: the spread grid fades in at the viewport edges, then travels
       inward and settles as the centered lockup (the END resting form). Both beats
       move every glyph together — no stagger. Holds, then (on real readiness) fades
       the wordmark out and splits the curtain open. */
    run() {
      var self = this;
      this.classList.add('is-animating');
      this.measure();
      this.computeCoords();
      var sc = this.coords;
      var k = this.duration / 2800; /* merchant duration scales the whole intro */

      // Late boot — the static wordmark has already revealed (slow link) or the page
      // finished loading before this script arrived. Skip the intro and show the
      // centered lockup immediately, so the visitor isn't shown a full intro after
      // they've already been waiting.
      var nowTs = (window.performance && performance.now) ? performance.now() : Date.now();
      var elapsed = nowTs - (window.__springPreloaderStart || nowTs);
      if (document.readyState === 'complete' || elapsed > 500) {
        this.runAssembled();
        return;
      }

      // Resolves once the glyphs have settled into the centered lockup (meaningful min display).
      var resolveMin;
      var minDisplay = new Promise(function (res) { resolveMin = res; });

      // Hold the first frame via inline styles, then run both beats per glyph:
      // Beat 1 — fade the spread viewport-edge grid in (all glyphs together, no stagger).
      // Beat 2 — travel inward to the centered lockup (all together).
      this.glyphs.forEach(function (g, i) {
        g.style.transform = transformFor(sc.scatter[i]);
        g.style.opacity = '0';
        self._anims.push(g.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: 600 * k, easing: EASE_OUT, fill: 'both' }
        ));
        self._anims.push(g.animate(
          [{ transform: transformFor(sc.scatter[i]) }, { transform: transformFor(sc.gather[i]) }],
          { duration: 900 * k, delay: 850 * k, easing: EASE_INOUT, fill: 'both' }
        ));
      });

      // Hold the settled lockup a beat before allowing the reveal.
      this._minTimer = setTimeout(resolveMin, (850 + 900 + 300) * k);

      this.setupProgress();

      var ready = Promise.all([minDisplay, pageLoadedPromise(), fontsReadyPromise()]).then(function () { return 'ready'; });
      Promise.race([ready, timeoutPromise(this.maxWait, 'timeout')]).then(function () {
        self.lift();
      });
    }

    /* Late boot: place the centered (END) lockup immediately, hold briefly, then
       reveal once ready. No fade-in / travel intro. */
    runAssembled() {
      var self = this;
      this.classList.add('is-animating');
      this.measure();
      this.computeCoords();
      var sc = this.coords;
      this.glyphs.forEach(function (g, i) {
        g.style.transform = transformFor(sc.gather[i]);
        g.style.opacity = '1';
      });
      this.setupProgress();
      var ready = Promise.all([pageLoadedPromise(), fontsReadyPromise()]);
      Promise.all([ready, timeoutPromise(300)]).then(function () { self.lift(); });
      timeoutPromise(this.maxWait).then(function () { self.lift(); });
    }

    /* Exit: jump any in-flight intro to its end frame, complete the indicator,
       then hand off to the .is-leaving CSS — the wordmark fades and the curtain
       panels split open (top/bottom on desktop, left/right on mobile). */
    lift() {
      var self = this;
      try {
        if (this._lifted) return;
        this._lifted = true;
        if (this._progressTick) { clearInterval(this._progressTick); this._progressTick = null; }
        if (this._minTimer) { clearTimeout(this._minTimer); this._minTimer = null; }
        this._anims.forEach(function (a) { try { a.finish(); } catch (e) {} });
        this.setProgress(1);
        this.classList.add('is-leaving');
        setTimeout(function () { self.cleanup(false); }, 1050);
      } catch (err) {
        self.cleanup(true);
      }
    }

    /* prefers-reduced-motion / no-WAAPI: show the wordmark statically, wait for
       readiness, quick fade. */
    runReduced() {
      var self = this;
      // The static CSS wordmark is already centered and shown (no .is-animating class).
      // Just track real progress, wait for readiness, then fade the curtain.
      this.setupProgress();
      var ready = Promise.all([pageLoadedPromise(), fontsReadyPromise()]);
      Promise.race([ready, timeoutPromise(this.maxWait, 'timeout')]).then(function () {
        if (self._lifted) return;
        self._lifted = true;
        if (self._progressTick) { clearInterval(self._progressTick); self._progressTick = null; }
        self.setProgress(1);
        self.style.transition = 'opacity 0.3s ease';
        self.style.opacity = '0';
        setTimeout(function () { self.cleanup(false); }, 320);
      });
    }

    cleanup(immediate) {
      try { clearTimeout(window.__springPreloaderFailsafe); } catch (e) {}
      if (this._progressTick) { clearInterval(this._progressTick); this._progressTick = null; }
      if (this._counterRaf) { cancelAnimationFrame(this._counterRaf); this._counterRaf = null; }
      if (this.docEl) { this.docEl.classList.remove('spring-preloading'); }
      if (immediate) { this.style.display = 'none'; }
      if (this.parentNode) { this.parentNode.removeChild(this); }
      try { document.dispatchEvent(new CustomEvent('spring:preloader:done')); } catch (e) {}
    }
  }

  customElements.define('spring-preloader', SpringPreloader);
})();
